import { pick } from 'underscore'
import Events from '@/configuration/Events'
import storeConfiguration from '@/configuration/StoreConfiguration'
import {
  isPaymentAnswer,
  isTransferAnswer,
  getStatus
} from '@/common/util/apiAnswer'
export default class PaymentManager {
  eventListenerFn

  constructor($locator) {
    this.$locator = $locator

    // Remove possibly added event listener on the ghost window when its closed
    $locator.$bus.$on(Events.krypton.message.finishedRedirection, () => {
      window.removeEventListener('message', this.eventListenerFn)
    })
  }

  addMessageListener(callback = () => {}, isSmartFormRedirection = false) {
    this.eventListenerFn = this._getEventListener(
      callback,
      isSmartFormRedirection
    )
    window.addEventListener('message', this.eventListenerFn, false)
  }

  handler(answer, isSmartFormRedirection = false) {
    const { $store } = this.$locator
    // If popupPayment, force answer to json
    if (answer._name === 'popupPayment') answer = answer.json()
    const { formId } = answer

    if (isPaymentAnswer(answer?.clientAnswer)) {
      this.handlePayment(answer, isSmartFormRedirection)
    } else if (isTransferAnswer(answer?.clientAnswer)) {
      this.handleTransfer(answer, isSmartFormRedirection)
    } else {
      // Unexpected response, look for error info
      $store.dispatch('error', { formId, metadata: { answer } })
    }
  }

  handlePayment(answer, isSmartFormRedirection) {
    const { application, $store, proxy, storeFactory } = this.$locator
    const { formId } = answer
    const isSmartPayment = isSmartFormRedirection || application !== 'ghost'
    const validAnswer =
      answer.hash && answer.hashAlgorithm && (answer._type || answer.type)

    switch (getStatus(answer.clientAnswer).toLowerCase()) {
      case 'unpaid':
        $store.dispatch('error', { formId, metadata: { answer } })
        break
      case 'paid':
      case 'running':
        if (validAnswer) {
          const paymentStore = this._createPaymentStore(answer, formId)
          if (isSmartPayment) $store.dispatch('saveTransaction', paymentStore)
          else proxy.send(paymentStore)
        }

        break
      case 'partially_paid':
        if (answer.clientAnswer.transactions[0].status === 'PAID') {
          $store.dispatch('update', {
            partialPayment: { processingPartialPayment: true }
          })
          this._sendPartialPayment(
            storeFactory.create(
              'billingPartialPayment',
              this._createPaymentStore(answer, formId)
            )
          )
        } else $store.dispatch('error', { formId, metadata: { answer } })
    }
  }

  handleTransfer(answer, isSmartFormRedirection) {
    const { application, $store, proxy } = this.$locator
    const { formId } = answer
    const isSmartPayment = isSmartFormRedirection || application !== 'ghost'
    const validAnswer =
      answer.hash && answer.hashAlgorithm && (answer._type || answer.type)

    switch (getStatus(answer.clientAnswer).toLowerCase()) {
      case 'transferred':
      case 'running':
        if (validAnswer) {
          const paymentStore = this._createPaymentStore(answer, formId)
          if (isSmartPayment) $store.dispatch('saveTransaction', paymentStore)
          else proxy.send(paymentStore)
        }
        break
    }
  }

  _sendPartialPayment(paymentStore) {
    const { application, $bus, proxy } = this.$locator

    if (application === 'host')
      $bus.$emit(Events.krypton.message.billingPartialPayment, paymentStore)
    else proxy.send(paymentStore)
  }

  _getEventListener(callback, isSmartFormRedirection = false) {
    return ({ data }) => {
      // Here we can be receiving a string prefixed by /*krypton-client*/ sent by the communicator
      // We don't care about these messages
      if (typeof data === 'string') return

      // The postMessages sent by the iframe content have the following format: { data: string, action: string }
      if (data.action === 'validate') {
        const answerData = data.data.replace(
          /"clientAnswer":"({.*})"/,
          '"clientAnswer":$1'
        )
        this.handler(JSON.parse(answerData), isSmartFormRedirection)
        callback()
      } else if (data.action === 'close') {
        const { application: app, $store, storeFactory, proxy } = this.$locator
        // ignore close if partial payment is being processed
        // the process is asynchronous and the close event can be triggered before the process has finished
        if ($store.state.partialPayment.processingPartialPayment) return

        // For popup, dispatch the action to close and trigger the redirection
        if (app === 'host') $store.dispatch('closeWindow')
        // For iframe, send the action to navigate back and dispatch aborted error
        else {
          proxy.send(storeFactory.create('navigateBack'))

          // Required to not show the error when the receipt is displayed
          if (!$store.state.redirectTransaction)
            $store.dispatch('error', $store.getters.abortedErrorCode)
        }
      }
    }
  }

  _createPaymentStore(answer, formId) {
    const payment = pick(answer, storeConfiguration.payment.fields)
    payment.formId = formId
    if (!payment.extra) payment.extra = {}
    if (answer.hashKey) payment.hashKey = answer.hashKey
    if (!payment.rawClientAnswer && answer?.clientAnswer)
      payment.rawClientAnswer = JSON.stringify(answer.clientAnswer)
    return this.$locator.storeFactory.create('payment', payment)
  }
}
